/* eslint-disable */
// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import { Layout, Header, Card, Button } from '../components/mte/shell';
import { HERO_CELLS, type HeroCell, type HeroId } from '../components/mte/placeholders';
import { getActiveProfile, type Profile } from '../lib/profiles';
import {
  selectActivities,
  loadConstellation,
  saveResult,
  getDiagnosticStorageKey,
  type Constellation,
  type NiveauDiagnostic,
} from '../lib/diagnostic-runtime';
import { BANQUE_CP } from '../data/banque-questions-cp';
import { BANQUE_CE1 } from '../data/banque-questions-ce1';
import { BANQUE_CE2 } from '../data/banque-questions-ce2';
import { BANQUE_CM1 } from '../data/banque-questions-cm1';
import {
  resolveBankForTarget,
  TARGET_TO_PREVIOUS_BANK,
  type TargetLevel,
  type PreviousLevel,
} from '../lib/diagnostic-runtime';
import type { BanqueItem } from '../lib/banque-types';
import { TexteATrou } from '../components/mte/items/TexteATrou/TexteATrou';
import { Ordre } from '../components/mte/items/Ordre/Ordre';
import { Association } from '../components/mte/items/Association/Association';
import { ManipulationGlisser } from '../components/mte/items/ManipulationGlisser/ManipulationGlisser';
import { ProductionEcrite } from '../components/mte/items/ProductionEcrite/ProductionEcrite';
import { Dictee } from '../components/mte/items/Dictee/Dictee';
import { LectureVoixHaute } from '../components/mte/items/LectureVoixHaute/LectureVoixHaute';
import { AudioButton } from '../components/mte/AudioButton/AudioButton';
import { apiFetch } from '../lib/auth-fetch';
import styles from './Diagnostic.module.css';

/**
 * Diagnostic session persistence (bug fix round 4 — sortie involontaire).
 *
 * Pourquoi : le <Layout> enfant auto-render un <NavHub> dans le footer
 * (cf. components/mte/shell.tsx). Ce NavHub contient un <Link to="/famille/accueil/">
 * ("Choisis ton profil"). En cliquant (intentionnellement ou par erreur touch/click)
 * l'enfant quitte /enfant/diagnostic/ avant la fin -> ecran vide sur /famille/accueil/.
 *
 * Fix : (a) on passe un footer custom vide au Layout du Diagnostic pour supprimer
 * le NavHub sur cette route concentree. (b) on persiste l'etat de session en
 * localStorage pour que si une sortie involontaire survient, le diagnostic peut
 * reprendre exactement ou il en etait (childId, currentIdx, reponses deja
 * enregistrees, activities selectionnees, phase).
 */
const DIAGNOSTIC_SESSION_KEY = 'mte:diagnostic:session:v1';

type DiagnosticSessionState = {
  childId: string | null;
  currentIdx: number;
  answeredCompetences: string[];
  activitiesIds: string[];
  phase: Phase;
  banqueLevel: NiveauDiagnostic;
  targetLevel: TargetLevel;
  savedAt: number;
};

function loadDiagnosticSession(): DiagnosticSessionState | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(DIAGNOSTIC_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DiagnosticSessionState>;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      childId: parsed.childId ?? null,
      currentIdx: typeof parsed.currentIdx === 'number' ? parsed.currentIdx : 0,
      answeredCompetences: Array.isArray(parsed.answeredCompetences)
        ? parsed.answeredCompetences
        : [],
      activitiesIds: Array.isArray(parsed.activitiesIds) ? parsed.activitiesIds : [],
      phase: (['intro', 'question', 'result'] as Phase[]).includes(parsed.phase as Phase)
        ? (parsed.phase as Phase)
        : 'intro',
      banqueLevel: parsed.banqueLevel ?? 'CP',
      targetLevel: parsed.targetLevel ?? 'CE1',
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function saveDiagnosticSession(state: DiagnosticSessionState): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(DIAGNOSTIC_SESSION_KEY, JSON.stringify(state));
  } catch {
    // storage full / disabled - silent, non-bloqueant
  }
}

function clearDiagnosticSession(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(DIAGNOSTIC_SESSION_KEY);
  } catch {
    // silent
  }
}


// Mapping target → previous-level bank (Ana-direct 19/09 ~16:30 bug #1)
// Pattern universelle: target = CE1 → diagnostique acquis fin CP
const BANKS_BY_PREVIOUS: Partial<Record<PreviousLevel, ReadonlyArray<BanqueItem>>> = {
  CP: BANQUE_CP,
  CE1: BANQUE_CE1,
  CE2: BANQUE_CE2,
  CM1: BANQUE_CM1,
};

/**
 * Diagnostic - ecran 8/11 (D-NX-071 + D-NX-006 + D-NX-010, J.4 2026-09-14).
 *
 * Runtime selection 12-16 activites deterministes (Boussole J.2 section 8)
 * via lib/diagnostic-runtime.ts + banque canon (Lumen J.1 51 items / 42 competences).
 *
 * Marqueurs canon respectes (Ana-direct 14/09 I/II/III) :
 *   - 6 heros fixes (D-NX-038)
 *   - Sabio mentor UNIVERSEL, NON selectionnable (D-NX-042)
 *   - Diagnostic parallele, NON bloquant (D-MAP-02, D-NX-010)
 *   - Phrase Sage intro D-NX-001 : "Découvre tes pouvoirs, aventurier." (Variante A)
 *   - Phrase Sage outro D-DIAG-08 : "Merci {firstName}. On se retrouve sur la carte."
 *   - Encouragement par competence (contextuel, non generique)
 *   - Resultats sous forme NARRATIVE (jamais de pourcentage, D-OB-03)
 *   - Pas de "bonne/mauvaise reponse" (D-OB-03)
 *   - Pas de promesse chiffree (D-BP5-08)
 *   - AppBar Sortie parent TOUJOURS visible (D-UF-03)
 *   - Tap targets >= --touch-xl (96x96) sur cartes reponses (D-PAL-CL-03)
 *
 * Phase : `intro` | `question` | `result`.
 * Non-bloqueant : bouton "Plus tard" disponible a chaque etape (intro + entre questions + result).
 */

type Phase = 'intro' | 'question' | 'result';

const SAGE_INTRO_PHRASE = "Aujourd'hui, nous allons découvrir tes pouvoirs !"; // Ana-direct 21/09 (Diagnostic intro)
const SAGE_INTRO_BODY = 'Pour cela, tu vas faire quelques petites activités. Prends ton temps, fais de ton mieux… et voyons quels pouvoirs se cachent en toi !';
const SAGE_OUTRO_PHRASE = (firstName: string): string =>
  `Merci, ${firstName}. On se retrouve sur la carte.`; // D-DIAG-08 (Option 2)

// NARRATIVE_OUTCOMES supprimé (Ana-direct 19/09 ~16:30):
// le résultat affiche désormais le profil par compétence (10 domaines × S/C/F)
// plutôt que 3 outcomes thématiques (Français Savant / Maths Savant).
const NARRATIVE_OUTCOMES: ReadonlyArray<never> = [];

const HERO_BY_ID: ReadonlyMap<HeroId, HeroCell> = new Map(
  HERO_CELLS.filter((c) => c.id !== 'sage').map((c) => [c.id, c]),
);

const SAGE_CELL: HeroCell | undefined = HERO_CELLS.find((c) => c.id === 'sage');

const HERO_MEDALLIONS: Partial<Record<HeroId, string>> = {
  ninja: '/choix-heros/medallion-ninja.webp',
  lobo: '/choix-heros/medallion-loup.webp',
  princesa: '/choix-heros/medallion-princesse.webp',
  fada: '/choix-heros/medallion-fee.webp',
  gato: '/choix-heros/medallion-chat.webp',
  dragao: '/choix-heros/medallion-dragon.webp',
};


function getHero(id: HeroId | null): HeroCell {
  if (!id) {
    return HERO_CELLS[0] as HeroCell;
  }
  const cell = HERO_BY_ID.get(id);
  if (!cell) {
    return HERO_CELLS[0] as HeroCell;
  }
  return cell;
}

function encouragementForCompetence(competence: string): string {
  // D-DIAG-07 : encouragement par competence (contextuel au archetype/competence)
  if (competence.startsWith('FR-LEC')) return 'Continue, tu lis bien !';
  if (competence.startsWith('FR-ECR')) return 'Prends ton temps pour écrire.';
  if (competence.startsWith('FR-ORTH')) return 'Écoute bien les sons.';
  if (competence.startsWith('FR-CG')) return 'Bien réfléchi !';
  if (competence.startsWith('FR-GR')) return 'Courage, tu progresses !';
  if (competence.startsWith('FR-LEX')) return 'Continue !';
  if (competence.startsWith('MA-NB')) return 'Tu calcules bien !';
  if (competence.startsWith('MA-GM')) return 'Bonne mesure !';
  if (competence.startsWith('MA-GEO')) return 'Tu vois bien les formes !';
  if (competence.startsWith('MA-OGD')) return 'Bien organisé !';
  return 'On réessaye ensemble ?'; // D-XR-08 default
}

export default function Diagnostic(): JSX.Element {
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('childId');
  const localActive = getActiveProfile();
  const [serverActive, setServerActive] = useState<Profile | null>(null);
  const active = serverActive ?? localActive;
  const activeId = active?.id;

  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    void apiFetch<{ childId: string; firstName: string; schoolLevel: 'CE1' | 'CM1'; heroId: HeroId | null }>(
      `/api/parent/children/${encodeURIComponent(childId)}`,
    ).then((child) => {
      if (cancelled) return;
      setServerActive({
        id: child.childId,
        firstName: child.firstName,
        schoolLevel: child.schoolLevel,
        heroId: child.heroId,
        createdAt: '',
      });
    }).catch((error) => {
      console.warn('[diagnostic] child profile fetch failed', error);
    });
    return () => { cancelled = true; };
  }, [childId]);
  const [phase, setPhase] = useState<Phase>('intro');
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  const hero = getHero(active?.heroId ?? null);
  const niveau: NiveauDiagnostic = (active?.schoolLevel ?? 'CE1') as NiveauDiagnostic;

  // Selection runtime au mount (intro OK + passe en question)
  const [activities, setActivities] = useState<BanqueItem[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answeredCompetences, setAnsweredCompetences] = useState<Set<string>>(new Set());

  // Resume previous session on mount (bug fix round 4: sortie involontaire).
  // Si l'enfant a quitté accidentellement, l'etat est restaure.
  useEffect(() => {
    const saved = loadDiagnosticSession();
    if (!saved) return;
    if (saved.phase === 'result') {
      // Session deja terminee -> on nettoie et on laisse l'intro normale
      clearDiagnosticSession();
      return;
    }
    setPhase(saved.phase);
    setCurrentIdx(saved.currentIdx);
    setAnsweredCompetences(new Set(saved.answeredCompetences));
    // Les activities seront re-selectees par le useEffect selectActivities
    // puis le currentIdx ramenera l'enfant directement a la question en cours.
  }, []);

  // Diagnostic cible les acquis de N-1 (target = niveau = current grade child)
  const targetLevel: TargetLevel = (niveau as TargetLevel);
  // Bank level = TARGET_TO_PREVIOUS_BANK[targetLevel] — the level items actually belong to.
  // selectActivities filters pool by schoolLevel, so we MUST pass the bank's level, not the child's grade.
  const banqueLevel: NiveauDiagnostic = TARGET_TO_PREVIOUS_BANK[targetLevel];
  const banque = useMemo(
    () => BANKS_BY_PREVIOUS[banqueLevel] ?? [],
    [banqueLevel],
  );

  // Re-select when entering question phase (idempotent on profileId)
  useEffect(() => {
    if (phase !== 'question' || activities.length > 0) return;
    if (!activeId) return;
    const sessionHistory = loadConstellation(activeId).sessionHistory;
    const result = selectActivities({
      profileId: activeId,
      schoolLevel: banqueLevel,
      targetLevel,
      pool: banque,
      sessionHistory,
    });
    setActivities((prev) => {
      if (prev.length === result.activities.length &&
          prev.every((a, i) => a.id === result.activities[i]?.id)) {
        return prev;
      }
      return result.activities;
    });
  }, [phase, activeId, niveau, banque, activities.length]);

  // Persist session on every state change (bug fix round 4: sortie involontaire).
  useEffect(() => {
    if (phase === 'intro' && currentIdx === 0 && answeredCompetences.size === 0 && activities.length === 0) {
      // Pas de session a persister encore (intro tout neuf).
      return;
    }
    saveDiagnosticSession({
      childId: childId ?? null,
      currentIdx,
      answeredCompetences: Array.from(answeredCompetences),
      activitiesIds: activities.map((a) => a.id),
      phase,
      banqueLevel,
      targetLevel,
      savedAt: Date.now(),
    });
  }, [phase, currentIdx, answeredCompetences, activities, childId, banqueLevel, targetLevel]);

  // Cleanup on result phase completion.
  useEffect(() => {
    if (phase === 'result') {
      // On garde l'etat pour 60s en cas de retour rapide, puis on nettoie.
      const timer = window.setTimeout(() => clearDiagnosticSession(), 60_000);
      return () => window.clearTimeout(timer);
    }
  }, [phase]);

  const current = activities[currentIdx];

  // Calcul resultat constellation au finish
  const finalConstellation = useMemo<Record<string, Constellation>>(() => {
    if (phase !== 'result' || !activeId) return {};
    return loadConstellation(activeId).constellation;
  }, [phase, activeId]);

  const appBar = (
    <Header
      context="enfant"
      user={
        <div className={styles.appBar} data-testid="app-bar">
          <div className={styles.appBarIdentity}>
            <span className={styles.appBarHero}><HeroIllustration cell={hero} /></span>
            <p className={styles.appBarHello}>
              <span className={styles.appBarHelloAccent}>{active?.firstName ?? 'Aventurier'}</span>
              {' - '}
              {hero.label}
            </p>
          </div>
          <Link
            to={childId ? `/enfant/aventure/?childId=${encodeURIComponent(childId)}` : '/enfant/aventure/'}
            className={styles.sortieLink}
            data-action="sortie-parent"
            aria-label="Sortie parent"
          >
            <Button tone="ghost" size="md" context="enfant">
              Sortie parent
            </Button>
          </Link>
        </div>
      }
    />
  );

  async function handleQuestionResult(correct: boolean, payload?: unknown) {
    if (!current || !active) return;
    const result: Constellation = correct ? 'S' : 'C';
    saveResult(active.id, current.competence, result, current.archetype);
    if (childId) {
      try {
        await apiFetch(`/api/device/children/${encodeURIComponent(childId)}/diagnostics`, {
          method: 'POST',
          body: JSON.stringify({ competenceId: current.competence, scoreState: result }),
        });
      } catch (error) {
        console.warn('[diagnostic] D1 persistence failed', error);
      }
    }
    setAnsweredCompetences((prev) => new Set(prev).add(current.competence));
    // Auto-advance after feedback
    setTimeout(() => {
      if (currentIdx + 1 < activities.length) {
        setCurrentIdx(currentIdx + 1);
        setSelectedOptionId(null);
      } else {
        setPhase('result');
      }
    }, 1500);
  }

  return (
    <Layout context="enfant" header={appBar} width="wide" footer={<></>}>
      <div className={styles.diagnosticScene} data-scene="diagnostic-v2">
        {/* Zone B — Header / Progress (Block 3 minimal integration)
            appBar remains in Layout.header (not moved here — that was the b60f6b9 pattern). */}
        <header className={styles.header} data-zone="header">
          <p className={styles.headerEyebrow}>Diagnostic</p>
          <p className={styles.headerSubtitle}>On fait le point pour bien commencer&nbsp;!</p>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={Math.max(activities.length, 1)}
            aria-valuenow={currentIdx + 1}
            aria-label={`Progression du diagnostic`}
            data-testid="diagnostic-progress"
          >
            <span
              className={styles.progressFill}
              style={{
                width: `${
                  activities.length > 0
                    ? Math.round(((currentIdx + 1) / activities.length) * 100)
                    : 0
                }%`,
              }}
            />
          </div>
          <p
            className={styles.screenLabel}
            aria-label={`Question ${currentIdx + 1} sur ${activities.length}`}
            data-testid="question-counter"
          >
            Question {currentIdx + 1} sur {activities.length}
          </p>
        </header>

        {/* Zone C — Hero illustration gauche (Block 5 visual integration).
            Scene-level hero decoration: avatar + encouragement bubble + decorative books.
            The active hero is cached from /onboarding/choix-heros/ (D-NX-038, D-NX-041). */}
        <aside
          className={styles.hero}
          data-zone="hero"
          aria-label={phase === 'intro' ? 'Sábio, guide du parcours' : `Héros de ${active?.firstName ?? 'Aventurier'} - ${hero.label}`}
          data-hero-zone={phase === 'intro' ? 'sage' : 'child'}
        >
          <div className={styles.heroAvatar} data-testid="hero-zone-avatar">
            {phase === 'intro' ? (
              <HeroIllustration cell={SAGE_CELL as HeroCell} pose="idle" />
            ) : (
              <HeroIllustration cell={hero} pose="neutral" />
            )}
          </div>
          <p className={styles.heroBubble} role="note" data-testid="hero-zone-bubble">
            {phase === 'intro' ? 'Je serai ton guide pendant cette aventure.' : 'Tu vas y arriver !'}
          </p>
          <span className={styles.heroBooks} aria-hidden="true" data-testid="hero-zone-books" />
        </aside>

        {/* Zone D — Card central (intro / question / result / loading phases).
            Zone E (decor) is populated below — Block 5 visual integration. */}
        <div className={styles.card} data-zone="card">
          <div className={styles.cardInner}>
            <main className={styles.diagnosticPage}>
              <div className={styles.sceneBackdrop} aria-hidden="true" />

      {phase === 'intro' && (
        <IntroPhase
          activeName={active?.firstName ?? 'Aventurier'}
          heroLabel={hero.label}
          banqueCount={banque.length}
          niveauLabel={niveau}
          onStart={() => {
            setPhase('question');
            setCurrentIdx(0);
            setAnsweredCompetences(new Set());
            setActivities([]); // force re-select on next render
          }}
          onLater={() => { /* non-bloqueant per D-NX-010 */ }}
        />
      )}

      {phase === 'question' && current && (
        <ActivityErrorBoundary
          activity={current}
          onSkip={() => {
            if (currentIdx + 1 < activities.length) {
              setCurrentIdx(currentIdx + 1);
              setSelectedOptionId(null);
            } else {
              setPhase('result');
            }
          }}
          onBack={() => {
            setPhase('intro');
            setCurrentIdx(0);
            setActivities([]);
            setAnsweredCompetences(new Set());
          }}
        >
          <QuestionPhase
            activity={current}
            index={currentIdx}
            total={activities.length}
            hero={hero}
            firstName={active?.firstName ?? 'Aventurier'}
            selectedOptionId={selectedOptionId}
            niveau={niveau}
            onSelect={setSelectedOptionId}
            onAnswer={handleQuestionResult}
            onSkip={() => {
              if (currentIdx + 1 < activities.length) {
                setCurrentIdx(currentIdx + 1);
                setSelectedOptionId(null);
              } else {
                setPhase('result');
              }
            }}
          />
        </ActivityErrorBoundary>
      )}

      {phase === 'question' && !current && (
        <LoadingPhase />
      )}

      {phase === 'result' && (
        <ResultPhase
          firstName={active?.firstName ?? 'Aventurier'}
          heroLabel={hero.label}
          constellation={finalConstellation}
          answeredCount={answeredCompetences.size}
          totalCount={activities.length}
          onBack={() => {
            setPhase('intro');
            setCurrentIdx(0);
            setActivities([]);
          }}
        />
      )}
            </main>
          </div>
        </div>

        {/* Zone E — Decor droite (Block 5 visual integration).
            Pure CSS decorative shapes (owl + books + banner + crystals + scroll).
            pointer-events: none inherited from .decor so it never blocks card clicks. */}
        <aside
          className={styles.decor}
          data-zone="decor"
          aria-hidden="true"
          data-testid="decor-zone"
        >
        </aside>

        {/* Zone F — Footer / nav (Block 4 of refactor v2).
            Persistent scene-level exit (« Plus tard »), non-bloquant per D-NX-010.
            Grid-area: footer (declared in Block 1 .diagnosticScene). */}
        <footer className={styles.footer} data-zone="footer">
          <div className={styles.footerNav}>
            <Link
              to="/enfant/hub/"
              className={styles.footerNavLink}
              data-action="plus-tard-footer"
              data-testid="footer-plus-tard"
            >
              Plus tard
            </Link>
          </div>
        </footer>
      </div>
    </Layout>
  );
}

/* ---------------------------------------------------------------------------
 * IntroPhase
 * ------------------------------------------------------------------------- */


/* ---------------------------------------------------------------------------
 * ActivityFallback - composant de repli si une activité échoue à rendre
 * Garantit que le diagnostic ne produit jamais écran blanc (M-C2.4 Q16->Q17 fix)
 * ------------------------------------------------------------------------- */

interface ActivityFallbackProps {
  activity: BanqueItem;
  onSkip: () => void;
  onBack: () => void;
}

function ActivityFallback({ activity, onSkip, onBack }: ActivityFallbackProps): JSX.Element {
  return (
    <section className={styles.error} data-phase="error-fallback" data-fallback="true">
      <Card>
        <h1 className={styles.errorTitle} data-testid="activity-fallback-title">
          Question temporairement indisponible
        </h1>
        <p className={styles.errorBody} data-testid="activity-fallback-body">
          L'activité "{activity.id}" ({activity.archetype}) n'a pas pu être chargée correctement.
          Tu peux passer cette question ou revenir à l'introduction.
        </p>
        <div className={styles.actions}>
          <Button
            tone="primary"
            size="md"
            context="enfant"
            block
            onClick={onSkip}
            data-action="skip-from-fallback"
            data-testid="activity-fallback-skip"
          >
            Passer cette question
          </Button>
          <Button
            tone="ghost"
            size="md"
            context="enfant"
            block
            onClick={onBack}
            data-action="back-from-fallback"
            data-testid="activity-fallback-back"
          >
            Revenir à l'introduction
          </Button>
        </div>
      </Card>
    </section>
  );
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

class ActivityErrorBoundary extends React.Component<
  { activity: BanqueItem; children: React.ReactNode; onSkip: () => void; onBack: () => void },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, errorMessage: '' };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: error?.message ?? String(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (typeof console !== 'undefined') {
      console.warn('[diagnostic] activity render failed', {
        activityId: this.props.activity?.id,
        archetype: this.props.activity?.archetype,
        message: error?.message,
        stack: info?.componentStack,
      });
    }
  }

  componentDidUpdate(prevProps: { activity: BanqueItem }): void {
    if (prevProps.activity?.id !== this.props.activity?.id && this.state.hasError) {
      this.setState({ hasError: false, errorMessage: '' });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <ActivityFallback activity={this.props.activity} onSkip={this.props.onSkip} onBack={this.props.onBack} />;
    }
    return this.props.children;
  }
}

function IntroPhase({
  activeName,
  heroLabel,
  banqueCount,
  niveauLabel,
  onStart,
  onLater,
}: {
  activeName: string;
  heroLabel: string;
  banqueCount: number;
  niveauLabel: string;
  onStart: () => void;
  onLater: () => void;
}): JSX.Element {
  return (
    <section className={styles.intro} data-phase="intro">
      <Card>
        <div className={styles.sageBlock} data-testid="sage-bubble">
          <div className={styles.sageSpeech}>
            <p className={styles.sagePhrase}>{SAGE_INTRO_PHRASE}</p>
            <p className={styles.sageSubphrase}>{SAGE_INTRO_BODY}</p>
          </div>
        </div>
        <p className={styles.introBankMeta} data-testid="intro-bank-meta">
          {`Banque : ${banqueCount} items couvrant les acquis de fin de ${niveauLabel}.`}
        </p>
      </Card>

      <div className={styles.introActions}>
        <Button tone="primary" size="xl" context="enfant" block onClick={onStart} data-action="commencer">
          Commencer
        </Button>
      </div>

      <p className={styles.reassurance}>
        Tu peux quitter à tout moment. Ce n'est pas une épreuve — c'est une rencontre.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * QuestionPhase - dispatch par archetype
 * ------------------------------------------------------------------------- */

function QuestionPhase({
  activity,
  index,
  total,
  hero,
  firstName,
  selectedOptionId,
  niveau,
  onSelect,
  onAnswer,
  onSkip,
}: {
  activity: BanqueItem;
  index: number;
  total: number;
  hero: HeroCell;
  firstName: string;
  selectedOptionId: string | null;
  niveau: NiveauDiagnostic;
  onSelect: (id: string) => void;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  onSkip: () => void;
}): JSX.Element {
  const encouragement = encouragementForCompetence(activity.competence);
  const [showAide, setShowAide] = useState(false);

  useEffect(() => {
    if (!showAide) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowAide(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showAide]);

  const progress = total > 0 ? Math.round(((index + 1) / total) * 100) : 0;
  return (
    <section className={styles.questionScene} data-phase="question">
      <aside className={styles.heroGuide} aria-label={`Héros de ${firstName}`}>
        <div className={styles.heroGuideBubble}>
          Tu es capable ! Prends ton temps et lis bien la question.
        </div>
        <div className={styles.heroGuideArt}>
          <HeroIllustration cell={hero} pose="standing" />
        </div>
      </aside>

      <div className={styles.questionCenter}>
        <section className={styles.progressHeader} aria-label={`Question ${index + 1} sur ${total}`}>
          <div className={styles.progressTitle}>
            <span className={styles.progressIcon} aria-hidden="true">◎</span>
            <span>
              <strong>Diagnostic</strong>
              <small>On fait le point pour bien commencer !</small>
            </span>
          </div>
          <div className={styles.progressTrackWrap}>
            <div className={styles.progressTrack} aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>
            <span className={styles.progressCount}>Question {index + 1} sur {total}</span>
          </div>
        </section>

        <div className={styles.questionCard}>
          <p className={styles.eyebrowSub}>
            Question {index + 1} sur {total}
          </p>
          <h1 className={styles.consigne} data-competence={activity.competence} data-archetype={activity.archetype}>
            {activity.enonce}
          </h1>
          <ItemAudioHeader item={activity} niveau={niveau} />

          <div className={styles.answerArea}>
            <ArchetypeRenderer
              activity={activity}
              selectedOptionId={selectedOptionId}
              onSelect={onSelect}
              onAnswer={onAnswer}
            />
          </div>

          {selectedOptionId && (
            <p className={styles.encouragement} role="status" data-testid="encouragement">
              {encouragement}
            </p>
          )}
        </div>

        <div className={styles.questionBottomNav}>
          <Button tone="secondary" size="md" context="enfant" data-action="skip" onClick={onSkip}>
            Passer cette question
          </Button>
        </div>

        {/* Aide button — Block 4 refactor: positioned INSIDE the central card
            (visually within .questionCenter, beside .questionBottomNav) so it
            sits in the card zone rather than as a right-column sibling.
            The modal Aide (helpOverlay/helpDialog/helpFooter) stays untouched
            and remains a sibling of .questionCenter to overlay correctly. */}
        <div className={styles.aideToggle} data-testid="aide-toggle">
        <button
          type="button"
          className={styles.aideButton}
          data-action="open-aide"
          data-testid="aide-button"
          aria-haspopup="dialog"
          aria-expanded={showAide}
          aria-controls="aide-dialog"
          onClick={() => setShowAide(true)}
        >
          <span aria-hidden="true">?</span>
          Besoin d'aide ?
        </button>
      </div>
    </div>

      {showAide && (
        <div
          className={styles.helpOverlay}
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setShowAide(false);
          }}
        >
          <section
            id="aide-dialog"
            className={styles.helpDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="aide-title"
            data-testid="aide-panel"
          >
            <header className={styles.helpHeader}>
              <img
                src="/mte-entree-2026-09-18/sabie-professeur-coruja-canon.png"
                alt=""
                aria-hidden="true"
                className={styles.helpSage}
              />
              <div className={styles.helpHeading}>
                <strong id="aide-title">Besoin d'un coup de pouce ?</strong>
                <span>Le professeur t'aide pour cette question.</span>
              </div>
              <button
                type="button"
                className={styles.helpClose}
                onClick={() => setShowAide(false)}
                aria-label="Fermer l'aide"
              >
                ×
              </button>
            </header>
            <div className={styles.helpContent}>
              <p>
                Relis doucement la consigne et cherche les mots qui te donnent un indice.
              </p>
              <p>
                Tu peux essayer une première réponse : le professeur est là pour t'aider à comprendre,
                pas pour te donner la réponse.
              </p>
            </div>
            <footer className={styles.helpFooter}>
              <button
                type="button"
                className={styles.helpDone}
                onClick={() => setShowAide(false)}
              >
                J'ai compris
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
}



function getItemAudioProps(item, niveau) {
  const meta = (item).meta || {};
  const poolLevel = TARGET_TO_PREVIOUS_BANK[niveau] || niveau;
  const ia = meta.instruction_audio || {};
  const sa = meta.stimulus_audio || {};
  const iaVal = ia[poolLevel] || ia[niveau];
  const saReq = !!(sa && sa.required);
  const consigneText = (item).consigne || item.enonce || '';
  return {
    instructionText: iaVal && iaVal !== 'absent' ? consigneText : null,
    stimulusText: saReq ? (sa.audio_text || consigneText) : null,
    stimulusRequired: saReq,
  };
}

function ItemAudioHeader({ item, niveau }) {
  const a = getItemAudioProps(item, niveau);
  if (!a.instructionText && !a.stimulusText) return null;
  return React.createElement('div', { className: styles.audioHeader, 'data-testid': 'item-audio-header' },
    a.instructionText ? React.createElement(AudioButton, { text: a.instructionText, kind: 'instruction' }) : null,
    a.stimulusText ? React.createElement(AudioButton, { text: a.stimulusText, kind: 'stimulus' }) : null,
  );
}

/* ---------------------------------------------------------------------------
 * ArchetypeRenderer - dispatch sur 7+ arche types
 * ------------------------------------------------------------------------- */

function ArchetypeRenderer({
  activity,
  selectedOptionId,
  onSelect,
  onAnswer,
}: {
  activity: BanqueItem;
  selectedOptionId: string | null;
  onSelect: (id: string) => void;
  onAnswer: (correct: boolean, payload?: unknown) => void;
}): JSX.Element {
  switch (activity.archetype) {
    case 'qcm': {
      const correct = selectedOptionId === activity.bonneReponse;
      return (
        <ul className={styles.options} role="radiogroup" aria-label={activity.enonce}>
          {activity.options.map((option, idx) => {
            const isSelected = selectedOptionId === option;
            const isCorrect = option === activity.bonneReponse;
            const showFeedback = isSelected;
            return (
              <li key={`${activity.id}-${idx}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={styles.optionCard}
                  data-option-id={option}
                  data-selected={isSelected ? 'true' : 'false'}
                  onClick={() => {
                    onSelect(option);
                    onAnswer(correct, { option });
                  }}
                >
                  <span className={styles.optionLabel}>{option}</span>
                  {showFeedback && (
                    <span className={isCorrect ? styles.feedbackOk : styles.feedbackBad} aria-live="polite">
                      {isCorrect ? 'OK' : 'X'}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      );
    }
    case 'vf': {
      const choices: Array<{ id: string; label: string; value: boolean }> = [
        { id: 'true', label: 'Vrai', value: true },
        { id: 'false', label: 'Faux', value: false },
      ];
      return (
        <ul className={styles.options} role="radiogroup" aria-label={activity.enonce}>
          {choices.map((c) => {
            const isSelected = selectedOptionId === c.id;
            const isCorrect = c.value === activity.bonneReponse;
            const showFeedback = isSelected;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={styles.optionCard}
                  data-option-id={c.id}
                  data-selected={isSelected ? 'true' : 'false'}
                  onClick={() => {
                    onSelect(c.id);
                    onAnswer(isCorrect, { value: c.value });
                  }}
                >
                  <span className={styles.optionLabel}>{c.label}</span>
                  {showFeedback && (
                    <span className={isCorrect ? styles.feedbackOk : styles.feedbackBad} aria-live="polite">
                      {isCorrect ? 'OK' : 'X'}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      );
    }
    case 'saisie': {
      return (
        <SaisieInput
          key={activity.id}
          consigne={activity.consigne}
          bonneReponse={activity.bonneReponse}
          tolerance={activity.tolerance ?? 0}
          onAnswer={onAnswer}
        />
      );
    }
    case 'texte-a-trou': {
      return <TexteATrou key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    case 'ordre': {
      return <Ordre key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    case 'association': {
      return <Association key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    case 'manipulation-glisser': {
      return <ManipulationGlisser key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    case 'production-ecrite': {
      return <ProductionEcrite key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    case 'dictee': {
      return <Dictee key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    case 'lecture-voix-haute': {
      return <LectureVoixHaute key={activity.id} item={activity} onAnswer={onAnswer} />;
    }
    default: {
      return <p className={styles.error}>Archetype non gere : {(activity as BanqueItem).archetype}</p>;
    }
  }
}

/* ---------------------------------------------------------------------------
 * SaisieInput - input libre avec tolerance Levenshtein
 * ------------------------------------------------------------------------- */

function SaisieInput({
  bonneReponse,
  tolerance,
  consigne,
  onAnswer,
}: {
  bonneReponse: string;
  tolerance: number;
  consigne?: string;
  onAnswer: (correct: boolean, payload?: unknown) => void;
}): JSX.Element {
  const [value, setValue] = useState('');
  function check() {
    const u = value.toLowerCase().normalize('NFC').trim().replace(/\s+/g, ' ');
    const e = bonneReponse.toLowerCase().normalize('NFC').trim().replace(/\s+/g, ' ');
    const isExact = u === e;
    let isCorrect = isExact;
    if (!isExact && tolerance > 0) {
      // Levenshtein tolerance
      isCorrect = levenshtein(u, e) <= tolerance;
    }
    onAnswer(isCorrect, { value });
  }
  return (
    <div className={styles.saisieBlock}>
      {consigne && (
        <p className={styles.saisieConsigne} data-testid="saisie-consigne">
          {consigne}
        </p>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={styles.saisieInput}
        data-testid="saisie-input"
        placeholder={consigne ? 'Tape ta réponse ici' : 'Tape ta reponse'}
        autoComplete="off"
      />
      <Button tone="primary" size="md" context="enfant" onClick={check} data-action="verifier-saisie">
        Verifier
      </Button>
    </div>
  );
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/* ---------------------------------------------------------------------------
 * LoadingPhase - etat transitoire quand activities pas encore selectees
 * ------------------------------------------------------------------------- */

function LoadingPhase(): JSX.Element {
  return (
    <section className={styles.loading} data-phase="loading">
      <Card>
        <p className={styles.loadingText}>On prepare tes questions...</p>
      </Card>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * ResultPhase - revelation narrative + constellation agregee
 * ------------------------------------------------------------------------- */

function ResultPhase({
  firstName,
  heroLabel,
  constellation,
  answeredCount,
  totalCount,
  onBack,
}: {
  firstName: string;
  heroLabel: string;
  constellation: Record<string, Constellation>;
  answeredCount: number;
  totalCount: number;
  onBack: () => void;
}): JSX.Element {
  const domains = aggregateForDisplay(constellation);
  return (
    <section className={styles.result} data-phase="result">
      <Card>
        <div className={styles.resultHeader}>
          <div>
            <p className={styles.resultEyebrow}>Bravo ! Tu as terminé ton diagnostic !</p>
            <h1 className={styles.resultTitle} data-testid="result-title">Ton héros a découvert ses pouvoirs</h1>
          </div>
        </div>

        <div className={styles.profileDomains} data-testid="profile-domains">
          <p className={styles.profileDomainsEyebrow}>Ta constellation :</p>
          <ul className={styles.profileDomainList}>
            {POWERS.map((p) => (
              <li key={p.key} data-power={p.key} data-status={domains[p.key] ?? 'N'}>
                <span className={styles.powerEmoji} aria-hidden="true">{p.emoji}</span>
                <strong>{p.label}</strong>
                <span className={styles.powerStatus}>{POWER_LABEL[domains[p.key] ?? 'N'] ?? ''}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className={styles.resultBody} data-testid="result-hero-attribution">
          {firstName} incarne {heroLabel}. Ton parcours s'adaptera à toi, étape après étape.
        </p>
        <p className={styles.resultBody} data-testid="result-counter">
          {answeredCount} questions répondues sur les {totalCount} prévues.
        </p>
        <p className={styles.resultSage} data-testid="sage-outro">
          {SAGE_OUTRO_PHRASE(firstName)}
        </p>
      </Card>

      <div className={styles.actions}>
        <Link to="/enfant/mission/rentree/carte/">
          <Button tone="primary" size="xl" context="enfant" block data-action="commencer-mon-parcours">
            Commencer mon parcours
          </Button>
        </Link>
        <Link to="/enfant/mes-pouvoirs/">
          <Button tone="secondary" size="md" context="enfant" block data-action="voir-mon-profil">
            Voir mon profil
          </Button>
        </Link>
        <Link to="/enfant/aventure/">
          <Button tone="ghost" size="md" context="enfant" block data-action="retour-carte">
            Retour à la carte
          </Button>
        </Link>
        <button
          type="button"
          className={styles.replay}
          onClick={onBack}
          data-action="refaire"
        >
          Refaire le diagnostic
        </button>
      </div>
    </section>
  );
}

// Per-competence profile (Ana-direct 19/09 ~16:30):
// cada compétence avaliada recebe label acquis / en progrès / à consolider / non évalué.
function labelForConstellation(val: Constellation): string {
  if (val === 'S') return 'Acquis';
  if (val === 'C') return 'En progrès';
  if (val === 'F') return 'À consolider';
  return 'Non évalué';
}

function aggregateForDisplay(constellation: Record<string, Constellation>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const comp of Object.keys(constellation).sort()) {
    out[comp] = labelForConstellation(constellation[comp]);
  }
  return out;
}

// Re-export Constellation to keep parity with runtime
export type { Constellation };
export { getDiagnosticStorageKey };
